import { AnyAction, combineReducers, Reducer } from 'redux';
import { IFeatureFlags } from 'types';
import { EmptyObject } from 'types/util/Object';
import localStorage from '../utils/localStorageUtils';
import activityReportReducers, { IStoreActivityReports } from './activityReports/ActivityReports';
import authenticationReducers from './authentication/authenticationReducers';
import { IStoreAuthentication } from './authentication/authenticationType';
import authMemberReducers from './authMember/authMemberReducers';
import { IStoreAuthMember } from './authMember/authMemberType';
import budgetReportReducer, { IStoreBudgetReport } from './budgetReports/BudgetReports';
import bulletinPostsReducers, { IStoreBulletinPosts } from './bulletinPosts/bulletinPostsReducers';
import { IStoreCostCodes } from './costCodes';
import costCodesReducers from './costCodes/costCodesReducers';
import dailyProjectReportReducers from './dailyReport/DailyReport';
import dailyReportAdditionalProgressPhotosReducers, {
  IStoreDailyReportAdditionalProgressPhotos,
} from './dailyReport/DailyReportAdditionalProgressPhotos';
import dailyReportAdditionalSafetyPhotosReducers, {
  IStoreDailyReportAdditionalSafetyPhotos,
} from './dailyReport/DailyReportAdditionalSafetyPhotos';
import dailyReportNewSectionsReducers, { IStoreDailyReportNewSections } from './dailyReport/DailyReportNewSections';
import { IStoreDailyReport } from './dailyReport/dailyReportType';
import dailySignInReducer, { IStoreDailySignIn } from './dailySignIn/DailySignIn';
import documentReportReducers, { IStoreDocumentReport } from './documentReport/DocumentReport';
import { IStoreEmployees } from './employees';
import employeesReducers from './employees/employeesReducers';
import employeeTimersReducer, { IStoreEmployeeTimers } from './employeeTimers/EmployeeTimers';
import { IStoreEnvironment } from './environment';
import environmentReducers from './environment/environmentReducers';
import featuresReducers from './features';
import gustoReducer, { IStoreGusto } from './gusto/gusto';
import costCodeLandingReducer, { IStoreCostCodeLandingPage } from './landingPages/CostCodeLandingPage';
import equipmentLandingReducer, { IStoreEquipmentLandingPage } from './landingPages/EquipmentLandingPage';
import projectLandingReducer, { IStoreProjectLandingPage } from './landingPages/ProjectLandingPage';
import launchDarklyReducer, { IStoreLaunchDarkly } from './launchDarkly/launchDarkly';
import manageGroupsReducer, { IStoreManageGroups } from './manageGroups/ManageGroups';
import manageModelMapsReducer, { IStoreManageModelMaps } from './manageModelMaps/ManageModelMaps';
import organizationReducers from './organization/organizationReducers';
import { IStoreOrganization } from './organization/organizationType';
import orgSubStatusReducers from './organizationSubscriptionStatus/organizationSubscriptionStatusReducers';
import { IStoreOrganizationSubscriptionStatus } from './organizationSubscriptionStatus/organizationSubscriptionStatusType';
import payStubsReportReducer, { IStorePayStubsReport } from './payStubsReport/PayStubsReport';
import { IStorePhotos } from './photos';
import photosReducers from './photos/photosReducers';
import preferencesReducer, { IStorePreferences } from './preferences/Preferences';
import { IStoreProjects } from './projects';
import projectsReducers from './projects/projectsReducers';
import sessionReducers from './session/sessionReducers';
import { IStoreSession } from './session/sessionType';
import tableScroll, { IStoreTableScroll } from './tableScroll/TableScroll';
import timeCardReportSettingsReducers, {
  IStoreTimeCardReportSettings,
} from './TimeCardReportSettings/TimeCardReportSettings';
import timeEntryTable, { IStoreTimeEntryTable } from './TimeEntryTable/TimeEntryTable';
import timeOffReportReducer, { IStoreTimeOffReport } from './timeOffReport/TimeOffReport';
import timesheetReducer, { IStoreTimesheets } from './timesheets/Timesheets';
import universalMapsReducer, { IStoreUniversalMaps } from './universalMaps/UniversalMaps';

export interface IReduxState {
  activityReports: IStoreActivityReports;
  authentication: IStoreAuthentication;
  authMember: IStoreAuthMember | EmptyObject;
  costCodeLanding: IStoreCostCodeLandingPage;
  costCodes: IStoreCostCodes;
  dailyReport: IStoreDailyReport;
  employees: IStoreEmployees;
  employeeTimers: IStoreEmployeeTimers;
  environment: IStoreEnvironment;
  equipmentLanding: IStoreEquipmentLandingPage;
  features: Required<IFeatureFlags>;
  gusto: IStoreGusto;
  launchDarkly: IStoreLaunchDarkly;
  manageGroups: IStoreManageGroups;
  organization: IStoreOrganization;
  organizationSubscriptionStatus: IStoreOrganizationSubscriptionStatus;
  photos: IStorePhotos;
  preferences: IStorePreferences;
  projectLanding: IStoreProjectLandingPage;
  projects: IStoreProjects;
  session: IStoreSession;
  tableScroll: IStoreTableScroll;
  timeOffReport: IStoreTimeOffReport;
  timesheet: IStoreTimesheets;
  universalMaps: IStoreUniversalMaps;
  timeEntryTable: IStoreTimeEntryTable;
  dailySignIn: IStoreDailySignIn;
  budgetReport: IStoreBudgetReport;
  manageModelMaps: IStoreManageModelMaps;
  payStubsReport: IStorePayStubsReport;
  dailyReportNewSections: IStoreDailyReportNewSections;
  dailyReportAdditionalProgressPhotos: IStoreDailyReportAdditionalProgressPhotos;
  dailyReportAdditionalSafetyPhotos: IStoreDailyReportAdditionalSafetyPhotos;
  documentReport: IStoreDocumentReport;
  timeCardReportSettings: IStoreTimeCardReportSettings;
  bulletinPosts: IStoreBulletinPosts;
}

export const appReducers = {
  activityReports: activityReportReducers,
  authentication: authenticationReducers,
  authMember: authMemberReducers,
  costCodeLanding: costCodeLandingReducer,
  costCodes: costCodesReducers,
  dailyReport: dailyProjectReportReducers,
  employees: employeesReducers,
  employeeTimers: employeeTimersReducer,
  environment: environmentReducers,
  equipmentLanding: equipmentLandingReducer,
  features: featuresReducers,
  gusto: gustoReducer,
  launchDarkly: launchDarklyReducer,
  manageGroups: manageGroupsReducer,
  organization: organizationReducers,
  organizationSubscriptionStatus: orgSubStatusReducers,
  photos: photosReducers,
  preferences: preferencesReducer,
  projectLanding: projectLandingReducer,
  projects: projectsReducers,
  session: sessionReducers,
  tableScroll,
  timeOffReport: timeOffReportReducer,
  timesheet: timesheetReducer,
  universalMaps: universalMapsReducer,
  timeEntryTable,
  dailySignIn: dailySignInReducer,
  budgetReport: budgetReportReducer,
  manageModelMaps: manageModelMapsReducer,
  payStubsReport: payStubsReportReducer,
  dailyReportNewSections: dailyReportNewSectionsReducers,
  dailyReportAdditionalProgressPhotos: dailyReportAdditionalProgressPhotosReducers,
  dailyReportAdditionalSafetyPhotos: dailyReportAdditionalSafetyPhotosReducers,
  documentReport: documentReportReducers,
  timeCardReportSettings: timeCardReportSettingsReducers,
  bulletinPosts: bulletinPostsReducers,
};

export const combinedReducers = combineReducers<IReduxState>(appReducers);

const LOGOUT = 'LOGOUT';
const storageKeysToRemove = ['authentication', 'stored_companies', 'gusto_payroll'];

export const rootReducer: Reducer<IReduxState, AnyAction> = (state = undefined, action) => {
  // NOTE: This doesn't use the standard switch() syntax
  if (action.type === LOGOUT) {
    storageKeysToRemove.forEach(localStorage.remove);
    state = undefined;
  }

  return combinedReducers(state, action);
};

export default rootReducer;
